/**
 * feat-361 — the "Move to Lesson…" picker. Shell is PublishGuardDialog's
 * (overlay, card, h3, lede, bordered scrolling list, Cancel + coral primary);
 * design authority is `design/02-move-picker.html` + `design/NOTES.md` §3/§7.
 *
 * NO FETCH: the lesson list is the editor's already-loaded `units`, so the dialog
 * opens fully populated and has no loading state (NOTES §3). The write happens
 * only on the primary button; `onMove` throws on failure and the dialog stays
 * open with the pick kept (state F). Success is closed by the owner.
 *
 * Deliberately NOT a <form>: Enter in the search field must not submit (§7).
 */

import React, { useEffect, useRef, useState } from 'react';
import type { QuizUnit } from '../../../services/quizContentAdminService';
import { UnitSwatch } from './MasterDetailTree';
import { formatReleaseInstant, isScheduledLater } from './lessonSchedule';
import { MOVE_DRILL_COPY as C, drillCount, moveErrorDetail } from './moveDrillCopy';

export interface MoveDrillDialogProps {
  drillTitle: string;
  /** The drill's current Lesson — listed, disabled, chipped "Current". */
  currentLessonId: string;
  /** Every Lesson in the tenant, in tree order. */
  units: QuizUnit[];
  glyphForKey: (key: string) => string;
  onCancel: () => void;
  /** Performs the move. Rejects on failure; the dialog then shows the error. */
  onMove: (targetLessonId: string) => Promise<void>;
}

const D = {
  overlay: 'fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4',
  card: 'bg-white rounded-2xl border border-softBorder shadow-xl w-full max-w-md p-6',
  h3: 'font-display text-lg font-semibold break-words',
  lede: 'text-sm text-inkMuted mt-1.5 leading-relaxed',
  search:
    'w-full text-sm bg-white border border-softBorder rounded-full pl-9 pr-3 py-2 placeholder:text-inkMuted/70 focus:outline-none focus:ring-2 focus:ring-coral/40 disabled:opacity-60',
  listBox: 'mt-3 border border-softBorder rounded-xl overflow-hidden',
  list: 'max-h-[18rem] overflow-y-auto divide-y divide-[rgba(0,0,0,0.06)]',
  row: 'flex items-center gap-2.5 px-3 py-2 text-sm cursor-pointer hover:bg-cream',
  rowSel: 'flex items-center gap-2.5 px-3 py-2 text-sm cursor-pointer bg-coral-light',
  rowCur: 'flex items-center gap-2.5 px-3 py-2 text-sm cursor-not-allowed bg-cream/60',
  radio: 'w-4 h-4 shrink-0 accent-coral',
  title: 'flex-1 min-w-0 font-semibold break-words line-clamp-2',
  meta: 'text-xs text-inkMuted shrink-0',
  current:
    'bg-white border border-softBorder text-inkMuted text-[10px] font-bold rounded-full px-1.5 py-0.5 shrink-0',
  hidden: 'bg-gray-100 text-inkMuted text-[10px] font-bold rounded-full px-1.5 py-0.5 shrink-0',
  later: 'bg-preview-light text-preview text-[10px] font-bold rounded-full px-1.5 py-0.5 shrink-0',
  dest: 'text-xs text-inkMuted mt-3 min-h-[1rem] break-words',
  err: 'mt-3 bg-danger-surface border border-danger-line/40 text-danger-text rounded-xl px-3 py-2 text-sm flex items-start gap-2',
  cancel: 'text-inkMuted text-sm font-semibold px-3 py-2 rounded-full hover:bg-gray-100 disabled:opacity-40',
  primary:
    'bg-coral text-white font-display font-semibold rounded-full px-5 py-2 text-sm hover:brightness-95 shrink-0 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2',
  empty: 'px-3 py-6 text-xs text-inkMuted text-center',
};

const FOCUSABLE = 'input:not([disabled]), button:not([disabled])';

export const MoveDrillDialog: React.FC<MoveDrillDialogProps> = ({
  drillTitle,
  currentLessonId,
  units,
  glyphForKey,
  onCancel,
  onMove,
}) => {
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const busyRef = useRef(false);
  busyRef.current = busy;

  // Initial focus on the search field (§7); give focus back to the invoker on
  // close — cancel, Esc and success alike.
  useEffect(() => {
    const invoker = document.activeElement as HTMLElement | null;
    searchRef.current?.focus();
    return () => {
      if (invoker && invoker.isConnected) invoker.focus();
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Esc is ignored while the move is in flight (state E).
        if (!busyRef.current) onCancel();
        return;
      }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      // Minimal focus wrap (§7) — PublishGuardDialog relies on aria-modal alone.
      const nodes = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        // A radio group is ONE tab stop: only the checked radio (or the first
        // enabled one) is reachable by Tab.
        (n) => !(n instanceof HTMLInputElement && n.type === 'radio'),
      );
      if (nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const others = units.filter((u) => u.id !== currentLessonId);
  const nowhereToGo = others.length === 0;
  const q = query.trim().toLowerCase();
  const shown = units.filter((u) => !q || (u.title || '').toLowerCase().includes(q));
  const pickedUnit = picked ? units.find((u) => u.id === picked) ?? null : null;

  const confirm = async () => {
    if (!picked || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onMove(picked);
      // Success: the owner unmounts the dialog. Nothing else to do here.
    } catch (e) {
      setError(moveErrorDetail(e));
      setBusy(false);
    }
  };

  const statusPill = (u: QuizUnit) =>
    !u.active ? (
      <span className={D.hidden}>{C.hidden}</span>
    ) : isScheduledLater(u) ? (
      <span className={D.later} title={`Available ${formatReleaseInstant(u.available_at)}`}>
        {C.later}
      </span>
    ) : null;

  const option = (u: QuizUnit) => {
    const title = u.title || '(untitled)';
    if (u.id === currentLessonId) {
      return (
        <label key={u.id} className={D.rowCur} aria-disabled="true">
          <input
            type="radio"
            name="move-drill-dest"
            className={D.radio}
            disabled
            data-testid={`move-drill-option-${u.id}`}
          />
          <span className="opacity-60 shrink-0 inline-flex">
            <UnitSwatch unit={u} glyphForKey={glyphForKey} />
          </span>
          <span className={`${D.title} text-inkMuted`}>{title}</span>
          <span className={D.current}>{C.current}</span>
          {statusPill(u)}
          <span className={D.meta}>{drillCount(u.drills.length)}</span>
        </label>
      );
    }
    const on = picked === u.id;
    return (
      <label key={u.id} className={`${on ? D.rowSel : D.row} ${busy ? 'pointer-events-none' : ''}`}>
        <input
          type="radio"
          name="move-drill-dest"
          className={D.radio}
          checked={on}
          disabled={busy}
          onChange={() => setPicked(u.id)}
          data-testid={`move-drill-option-${u.id}`}
        />
        <span className="shrink-0 inline-flex">
          <UnitSwatch unit={u} glyphForKey={glyphForKey} />
        </span>
        <span className={D.title}>{title}</span>
        {statusPill(u)}
        <span className={D.meta}>{drillCount(u.drills.length)}</span>
      </label>
    );
  };

  let dest: React.ReactNode;
  if (nowhereToGo) {
    dest = (
      <p className={D.dest} role="status">
        {C.nowhereToGo}
      </p>
    );
  } else if (pickedUnit) {
    const n = pickedUnit.drills.length;
    dest = (
      <p className={D.dest} data-testid="move-drill-destination">
        {n === 0 ? C.firstIn : C.addedAfter(n)}
        <strong className="text-ink font-semibold">{pickedUnit.title || '(untitled)'}</strong>.
      </p>
    );
  } else {
    dest = <p className={D.dest}>{C.chooseLesson}</p>;
  }

  return (
    <div
      className={D.overlay}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
      data-testid="move-drill-overlay"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="move-drill-title"
        aria-busy={busy ? 'true' : undefined}
        className={D.card}
        data-testid="move-drill-dialog"
      >
        <h3 id="move-drill-title" className={D.h3}>
          {C.title(drillTitle)}
        </h3>
        <p className={D.lede}>{C.lede}</p>

        <div className="relative mt-4">
          <input
            ref={searchRef}
            type="text"
            value={query}
            disabled={busy}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={C.searchPlaceholder}
            aria-label={C.searchLabel}
            className={D.search}
            data-testid="move-drill-search"
          />
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-inkMuted/60 text-sm">⌕</span>
        </div>

        <div className={`${D.listBox} ${busy ? 'opacity-60' : ''}`}>
          <div role="radiogroup" aria-label={C.listLabel} className={D.list}>
            {shown.length === 0 ? (
              <div className={D.empty} role="status">
                {C.noResults(query)}
              </div>
            ) : (
              shown.map(option)
            )}
          </div>
        </div>

        {dest}

        {error && (
          <div role="alert" className={D.err} data-testid="move-drill-error">
            <svg
              className="w-4 h-4 mt-0.5 shrink-0"
              aria-hidden="true"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" x2="12" y1="8" y2="12" />
              <line x1="12" x2="12.01" y1="16" y2="16" />
            </svg>
            <span>
              <strong className="font-semibold">{C.errorHead}</strong> {C.errorNothingChanged} {error}
            </span>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 mt-5">
          <button type="button" onClick={onCancel} disabled={busy} className={D.cancel}>
            {C.cancel}
          </button>
          <button
            type="button"
            onClick={() => void confirm()}
            disabled={!picked || busy || nowhereToGo}
            className={D.primary}
            data-testid="move-drill-confirm"
          >
            {busy ? (
              <>
                <span
                  className="w-3.5 h-3.5 rounded-full border-2 border-white border-t-transparent animate-spin inline-block"
                  aria-hidden="true"
                />
                {C.busy}
              </>
            ) : (
              C.confirm
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MoveDrillDialog;
